import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Sparkles } from "lucide-react";
import { Page } from "@/components/Page";
import { ProductCard } from "@/components/ProductCard";
import { api, resolveImg } from "@/lib/api";
import { iconFor } from "@/lib/categoryIcons";
import { useSettings } from "@/context/SettingsContext";
import { useSeo } from "@/lib/useSeo";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.7, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

const HeroImage = ({ src, className, y, delay = 0 }) => (
  <motion.div style={{ y }} className={className}>
    <img src={src} alt="" style={{ animationDelay: `${delay}s` }} className="w-full h-full object-cover rounded-[2rem] shadow-[0_20px_60px_rgba(112,0,255,0.25)]" />
  </motion.div>
);

const LAYOUTS = {
  1: ["top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[82%] h-[82%] rotate-1"],
  2: ["top-0 right-0 w-[62%] h-[62%] rotate-2", "bottom-0 left-0 w-[58%] h-[62%] -rotate-2"],
  3: ["top-0 right-0 w-[56%] h-[52%] rotate-2", "bottom-0 right-6 w-[44%] h-[44%] -rotate-3", "top-14 left-0 w-[46%] h-[56%] rotate-1"],
  4: ["top-0 left-[3%] w-[46%] h-[46%] -rotate-3", "top-[4%] right-0 w-[48%] h-[50%] rotate-2", "bottom-0 left-0 w-[47%] h-[47%] rotate-2", "bottom-[3%] right-[5%] w-[45%] h-[45%] -rotate-2"],
};

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]);
  const { settings } = useSettings();
  useSeo(settings?.seo?.home);
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 500], [0, -60]);
  const y2 = useTransform(scrollY, [0, 500], [0, 50]);
  const y3 = useTransform(scrollY, [0, 500], [0, -30]);
  const y4 = useTransform(scrollY, [0, 500], [0, 40]);
  const yArr = [y1, y2, y3, y4];

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 6)));
    api.get("/categories", { params: { featured: true } })
      .then((r) => setCategories(r.data))
      .catch(() => setCategories([]));
    api.get("/categories")
      .then((r) => setAllCategories(r.data))
      .catch(() => setAllCategories([]));
  }, []);

  const hero = settings?.hero || {};
  const heroImages = (hero.images && hero.images.length ? hero.images : [
    "https://images.pexels.com/photos/12698667/pexels-photo-12698667.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    "https://images.unsplash.com/photo-1723144567104-e2ca5d12bb4c?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  ]).slice(0, 4);
  const effect = hero.effect || "both";
  const positions = LAYOUTS[heroImages.length] || LAYOUTS[2];

  // An empty marquee falls back to the category names, so the strip stays
  // correct on its own instead of quietly listing categories that no longer
  // exist. All categories are used here, not only the featured ones.
  const marqueeTerms = settings?.marquee?.length
    ? settings.marquee
    : allCategories.map((c) => c.name);
  const pillars = settings?.pillars || [];

  return (
    <Page>
      {/* HERO */}
      <section className="relative grain overflow-hidden pt-32 md:pt-40 pb-16 md:pb-24 px-6 md:px-12">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-primary/20 blur-3xl animate-blob" />
        <div className="absolute top-40 right-0 w-96 h-96 rounded-full bg-secondary/20 blur-3xl animate-blob" style={{ animationDelay: "3s" }} />

        <div className="relative max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <motion.span
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground text-sm font-semibold px-4 py-2 mb-6"
            >
              <Sparkles size={15} /> {hero.badge || "Tu imaginas. Nós imprimimos."}
            </motion.span>

            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tighter">
              <span className="block overflow-hidden">
                <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block">
                  {hero.title || "Descobre um mundo de criatividade em"}
                </motion.span>
              </span>
              {hero.highlight && (
                <span className="block overflow-hidden">
                  <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block text-gradient">
                    {hero.highlight}
                  </motion.span>
                </span>
              )}
            </h1>

            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9 }}
              className="text-base md:text-lg text-foreground/70 mt-6 max-w-lg leading-relaxed"
            >
              {hero.subtitle || "Bonecos articulados, decoração, acessórios e produtos únicos criados com impressão 3D."}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05 }}
              className="flex flex-wrap gap-3 mt-8"
            >
              <Link to="/produtos" data-testid="explore-products-btn"
                className="inline-flex items-center gap-2 rounded-full brand-gradient text-white font-semibold px-7 py-4 transition-transform hover:scale-105 active:scale-95">
                Explorar Produtos <ArrowRight size={18} />
              </Link>
              <Link to="/contactos" data-testid="request-quote-btn"
                className="inline-flex items-center gap-2 rounded-full bg-foreground text-white font-semibold px-7 py-4 transition-transform hover:scale-105 active:scale-95">
                Pedir Orçamento
              </Link>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.4, duration: 0.7 }}
            className="relative h-[380px] md:h-[520px]"
          >
            {heroImages.map((src, i) => {
              const useFloat = effect === "both" || effect === "float";
              const useParallax = effect === "both" || effect === "parallax";
              return (
                <HeroImage
                  key={i}
                  src={src}
                  y={useParallax ? yArr[i % yArr.length] : undefined}
                  delay={i * 1.2}
                  className={`absolute ${positions[i]} ${useFloat ? "animate-float" : ""}`}
                />
              );
            })}
            {(hero.stat_value || hero.stat_label) && (
              <div className="absolute bottom-6 right-4 bg-white rounded-3xl shadow-xl px-5 py-4 border border-border z-10">
                <p className="font-display font-black text-2xl text-gradient">{hero.stat_value}</p>
                <p className="text-xs text-muted-foreground font-medium">{hero.stat_label}</p>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* MARQUEE — from settings, or the category names when left empty */}
      {marqueeTerms.length > 0 && (
        <section className="py-6 border-y border-border bg-foreground text-white">
          <Marquee speed={40} gradient={false} autoFill>
            {marqueeTerms.map((t, i) => (
              <span key={i} className="mx-8 font-display font-bold text-xl md:text-2xl inline-flex items-center gap-8">
                {t} <Sparkles className="text-secondary" size={20} />
              </span>
            ))}
          </Marquee>
        </section>
      )}

      {/* PILLARS — managed from the admin panel */}
      {pillars.length > 0 && (
        <section className="py-16 md:py-20 px-6 md:px-12 max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {pillars.map((p, i) => {
              const Icon = iconFor(p.icon);
              const color = p.color || "#7000FF";
              return (
                <motion.div key={`${p.label}-${i}`}
                  initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  data-testid={`pillar-${i}`}
                  className="flex flex-col items-center text-center gap-3 rounded-3xl border border-border bg-card p-6">
                  <span className="grid place-items-center w-14 h-14 rounded-2xl" style={{ backgroundColor: `${color}1a`, color }}>
                    <Icon size={26} />
                  </span>
                  <p className="font-display font-bold text-sm md:text-base">{p.label}</p>
                </motion.div>
              );
            })}
          </div>
        </section>
      )}

      {/* CATEGORIES — managed from the admin panel ("Destaque na homepage") */}
      {categories.length > 0 && (
        <section className="py-20 md:py-28 px-6 md:px-12 max-w-6xl mx-auto">
          <div className="flex items-end justify-between mb-10 gap-4">
            <div>
              <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight">Explora por categoria</h2>
              <p className="text-muted-foreground mt-3 text-lg">Há uma criação 3D perfeita para cada pessoa.</p>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categories.map((c, i) => {
              const Icon = iconFor(c.icon);
              return (
                <motion.div key={c.id}
                  initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: (i % 4) * 0.08 }}>
                  <Link to={`/produtos?cat=${encodeURIComponent(c.name)}`} data-testid={`home-cat-${i}`}
                    className="group block rounded-3xl border border-border bg-card overflow-hidden hover:bg-foreground hover:text-white transition-colors duration-300">
                    {c.image ? (
                      <div className="aspect-[4/3] overflow-hidden bg-muted">
                        <img src={resolveImg(c.image)} alt={c.name}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                      </div>
                    ) : null}
                    <div className="p-6">
                      {!c.image && (
                        <Icon className="mb-8 text-primary group-hover:text-secondary transition-colors" size={30} />
                      )}
                      <p className="font-display font-bold text-lg leading-tight">{c.name}</p>
                      <span className="text-sm text-muted-foreground group-hover:text-white/60 inline-flex items-center gap-1 mt-2">Ver <ArrowRight size={13} /></span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </section>
      )}

      {/* FEATURED */}
      <section className="py-8 md:py-16 px-6 md:px-12 max-w-6xl mx-auto">
        <div className="flex items-end justify-between mb-10 gap-4">
          <h2 className="font-display font-extrabold text-4xl md:text-5xl tracking-tight">Em destaque</h2>
          <Link to="/produtos" className="hidden md:inline-flex items-center gap-1 font-semibold text-primary hover:gap-2 transition-all">Ver tudo <ArrowRight size={16} /></Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 md:px-12 max-w-6xl mx-auto mt-8">
        <div className="relative overflow-hidden rounded-[3rem] brand-gradient text-white p-10 md:p-16 text-center">
          <div className="grain absolute inset-0" />
          <h2 className="relative font-display font-black text-3xl md:text-5xl tracking-tight">Tens uma ideia criativa?</h2>
          <p className="relative text-white/85 mt-4 text-lg max-w-xl mx-auto">Fazemos peças personalizadas. Fala connosco e transformamos a tua ideia em realidade impressa em 3D.</p>
          <Link to="/contactos" data-testid="cta-bottom" className="relative inline-flex items-center gap-2 mt-8 rounded-full bg-white text-foreground font-bold px-8 py-4 transition-transform hover:scale-105 active:scale-95">
            Pedir Orçamento <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </Page>
  );
}
